// src/pages/admin/SecurityPage.tsx
import React, { useEffect, useState } from "react";
import apiService from "../../services/ApiService";

// Local helper type (keeps this file standalone)
type ApiResponse<T> = { success: boolean; data: T; message?: string; error?: string };

export const SecurityPage = () => {
  const [fraudAlerts, setFraudAlerts] = useState<any[]>([]);
  const [securityDashboard, setSecurityDashboard] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSecurityData();
  
  }, []);

  const loadSecurityData = async () => {
    try {
      const [alertsRes, dashboardRes, sessionsRes] =
        (await Promise.all([
          apiService.getFraudAlerts(),
          apiService.getSecurityDashboard(),
          apiService.getSessions(),
        ])) as [ApiResponse<any[]>, ApiResponse<any>, ApiResponse<any[]>];

      setFraudAlerts(alertsRes?.data ?? []);
      // some backends already return a plain object instead of {success,data}
      setSecurityDashboard(dashboardRes?.data ?? dashboardRes);
      setSessions(sessionsRes?.data ?? []);
    } catch (err) {
      // sensible fallbacks
      setFraudAlerts([
        { id: "1", type: "multiple_scans", tagId: "NFC001", severity: "high", timestamp: "2024-01-20T10:00:00Z" },
        { id: "2", type: "gps_violation", tagId: "QR002", severity: "medium", timestamp: "2024-01-20T11:00:00Z" },
      ]);
      setSessions([
        { id: "1", userId: "user_001", ip: "192.168.1.1", device: "Chrome/Desktop", lastActive: "2024-01-20T12:00:00Z" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleReportFraud = async () => {
    const fraudData = {
      tagId: prompt("Enter Tag ID:"),
      description: prompt("Enter description:"),
      reportedBy: "current-user",
    };

    if (fraudData.tagId && fraudData.description) {
      try {
        await apiService.reportFraud(fraudData);
        alert("Fraud reported successfully!");
        loadSecurityData();
      } catch (err: any) {
        alert(`Error: ${err.message}`);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Security & Fraud Detection</h2>
          <p className="text-gray-600">Monitor security threats and fraud attempts</p>
        </div>
        <button onClick={handleReportFraud} className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700">
          Report Fraud
        </button>
      </div>

      {/* Security Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Alerts</p>
              <p className="text-3xl font-bold text-red-600">{fraudAlerts.length}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <span className="text-red-600 text-xl">🚨</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">High Severity</p>
              <p className="text-3xl font-bold text-red-600">
                {fraudAlerts.filter((a) => a.severity === "high").length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <span className="text-red-600 text-xl">⚠️</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Sessions</p>
              <p className="text-3xl font-bold text-blue-600">{sessions.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 text-xl">👥</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Security Score</p>
              <p className="text-3xl font-bold text-green-600">98%</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-green-600 text-xl">🛡️</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fraud Alerts Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Recent Fraud Alerts</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alert Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tag ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fraudAlerts.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{alert.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{alert.tagId}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        alert.severity === "high"
                          ? "bg-red-100 text-red-800"
                          : alert.severity === "medium"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                      }`}
                    >
                      {alert.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{new Date(alert.timestamp).toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button className="text-blue-600 hover:text-blue-900">Investigate</button>
                    <button className="text-red-600 hover:text-red-900">Block</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}
    </div>
  );
};

export default SecurityPage;
